import {cx} from '../../util';

/**
 * Shared sidebar geometry.
 *
 * The collapse animation is meant to read as the panel changing width around
 * stationary icons, not as its contents sliding. That only works if every row
 * — nav items, the brand lockup, the account row — puts its icon in an
 * identically sized slot at the same offset, and positions its label
 * absolutely so the label never contributes to layout. Then collapsing
 * animates exactly two things: the panel's width, and the labels' opacity.
 *
 * The numbers are tied together: a 56px rail with a 6px gutter leaves 44px,
 * so a 44px slot centres its icon on the rail's centre line (6 + 22 = 28 =
 * 56 / 2). The gutter must be identical in both states — if the rail used a
 * tighter one, every icon would slide sideways as the panel resized, which is
 * exactly what this arrangement exists to prevent. Narrowing the rail
 * therefore means shrinking the gutter and the rail together, keeping the slot
 * fixed. Change one number without the other and the icons will drift.
 */
export const RAIL_WIDTH_CLASS = 'w-[56px]';
export const SIDEBAR_WIDTH_CLASS = 'w-[264px]';

/** Horizontal padding shared by every column inside the sidebar. */
export const GUTTER_CLASS = 'px-1.5';

/** Fixed slot holding an icon, avatar or the logo mark. */
export const SLOT_CLASS = 'flex w-11 shrink-0 items-center justify-center';

/**
 * Left offset for absolutely positioned labels: a hair inside the 44px slot's
 * edge — flush with it read one pixel too airy next to the 20px line icons.
 */
export const LABEL_INSET_CLASS = 'left-[43px]';

/**
 * The wordmark sits a couple of pixels right of where nav labels start.
 *
 * Nav icons are 20px line art inside the 44px slot, so they carry their own
 * optical padding and a label flush against the slot edge still reads as
 * spaced. The logo mark is a 24px solid badge with none, so it needs the gap
 * made explicit: the mark ends at 40px, so this leaves an 8px gap, plus a
 * 1px drop to optically centre the wordmark against the mark. Aligning the
 * marks is what matters; aligning the wordmark with the labels below it
 * does not.
 */
export const BRAND_WORDMARK_INSET_CLASS = 'left-[48px] mt-px';

/**
 * Shared easing for the sidebar's width change and the panel's slide.
 *
 * A decelerating curve that starts fast and settles slowly: motion arrives
 * quickly enough to feel responsive, then eases out far more gently than
 * `ease-out` would, which is what reads as "smooth" rather than "abrupt". Both
 * surfaces use it so they look like one mechanism when they move together.
 */
export const EASING_CLASS = 'ease-[cubic-bezier(0.32,0.72,0,1)]';

/**
 * Press feedback. Rather than scaling the whole row — which drags the icon and
 * label around and reads as wobbly — only the background plate shrinks, so the
 * content stays put and the surface appears to give under the press.
 *
 * Insets rather than a transform on purpose. A uniform `scale` on a row that
 * is 240px wide and 40px tall moves the side edges six times further than the
 * top and bottom, so the squash is nearly invisible in the axis the eye
 * actually reads; a non-uniform scale would fix that but distort the corner
 * radius. Insetting shrinks both axes by real pixels and leaves the radius
 * intact.
 *
 * The timing is asymmetric: the plate snaps in quickly under the finger and
 * eases back out more slowly, which is what makes a button feel sprung rather
 * than merely animated.
 */
export const PRESS_PLATE_CLASS = `absolute inset-y-0 inset-x-px rounded-lg transition-all
  duration-150 ease-out group-active:inset-x-[2.5px] group-active:inset-y-[2px]
  group-active:duration-50`;

/**
 * Plate colours for a neutral row, including the darkening on press.
 *
 * The pressed tint is a lighter alpha of the next step on the grey ramp rather
 * than the step itself: composited over white it lands just below the hover
 * fill, so the press registers without the row appearing to change state.
 */
export const NEUTRAL_PLATE_CLASS = 'group-hover:bg-gray-100/80 group-active:bg-gray-200/50';

/**
 * Opacity transition for anything that only exists in the expanded sidebar.
 *
 * Fading in is delayed so the panel has started widening before text appears;
 * fading out is immediate so text is gone before the panel narrows onto it.
 * Without the asymmetry, labels visibly collide with the closing edge.
 */
export const labelTransition = (visible) => cx(
  'transition-opacity ease-out',
  visible
    ? 'opacity-100 duration-150 delay-100'
    : 'pointer-events-none opacity-0 duration-100 delay-0',
);
